/**
 * Performance Audit Script
 * Provides ongoing performance monitoring and recommendations
 */

(function() {
  'use strict';
  
  // Wait for page to load
  window.addEventListener('load', function() {
    setTimeout(runPerformanceAudit, 2000);
  });
  
  function runPerformanceAudit() {
    try {
      const audit = {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        performance: {},
        recommendations: [],
        issues: []
      };
      
      // Performance metrics
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation) {
        audit.performance = {
          dns: navigation.domainLookupEnd - navigation.domainLookupStart,
          tcp: navigation.connectEnd - navigation.connectStart,
          ttfb: navigation.responseStart - navigation.requestStart,
          domContentLoaded: navigation.domContentLoadedEventEnd - navigation.navigationStart,
          loadTime: navigation.loadEventEnd - navigation.navigationStart,
          firstPaint: 0,
          firstContentfulPaint: 0,
          largestContentfulPaint: 0
        };
      }
      
      // Paint timing
      const paintEntries = performance.getEntriesByType('paint');
      paintEntries.forEach(entry => {
        if (entry.name === 'first-paint') {
          audit.performance.firstPaint = entry.startTime;
        }
        if (entry.name === 'first-contentful-paint') {
          audit.performance.firstContentfulPaint = entry.startTime;
        }
      });
      
      // LCP
      const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
      if (lcpEntries.length > 0) {
        const lastLCP = lcpEntries[lcpEntries.length - 1];
        audit.performance.largestContentfulPaint = lastLCP.startTime;
      }
      
      // Resource analysis
      const resources = performance.getEntriesByType('resource');
      const resourceAnalysis = analyzeResources(resources);
      audit.resources = resourceAnalysis;
      
      // Generate recommendations
      generateRecommendations(audit);
      
      // Log results
      console.log('🚀 Performance Audit Results:', audit);
      
      // Send to analytics if available
      if (window.gtag) {
        window.gtag('event', 'performance_audit', {
          event_category: 'Performance',
          performance_metrics: audit.performance,
          recommendations_count: audit.recommendations.length,
          issues_count: audit.issues.length
        });
      }
      
      // Show recommendations in console
      if (audit.recommendations.length > 0) {
        console.log('💡 Performance Recommendations:');
        audit.recommendations.forEach((rec, index) => {
          console.log(`${index + 1}. ${rec.title}: ${rec.description}`);
        });
      }
      
      if (audit.issues.length > 0) {
        console.log('⚠️ Performance Issues:');
        audit.issues.forEach((issue, index) => {
          console.log(`${index + 1}. ${issue.title}: ${issue.description}`);
        });
      }
      
    } catch (error) {
      console.log('Performance audit error:', error);
    }
  }
  
  function analyzeResources(resources) {
    const analysis = {
      total: resources.length,
      byType: {},
      slowResources: [],
      largeResources: [],
      unoptimizedImages: []
    };
    
    resources.forEach(resource => {
      // Count by type
      const type = resource.initiatorType || 'other';
      analysis.byType[type] = (analysis.byType[type] || 0) + 1;
      
      // Slow resources (>1s)
      if (resource.duration > 1000) {
        analysis.slowResources.push({
          name: resource.name,
          duration: resource.duration,
          size: resource.transferSize || 0
        });
      }
      
      // Large resources (>500KB)
      if (resource.transferSize > 500000) {
        analysis.largeResources.push({
          name: resource.name,
          size: resource.transferSize,
          type: resource.initiatorType
        });
      }
      
      // Unoptimized images
      if (resource.initiatorType === 'img' && resource.transferSize > 200000) {
        analysis.unoptimizedImages.push({
          name: resource.name,
          size: resource.transferSize
        });
      }
    });
    
    return analysis;
  }
  
  function generateRecommendations(audit) {
    const { performance, resources } = audit;
    
    // TTFB recommendations
    if (performance.ttfb > 600) {
      audit.recommendations.push({
        title: 'Optimize TTFB',
        description: `TTFB is ${Math.round(performance.ttfb)}ms (should be <600ms). Consider server optimization, CDN, or database queries.`,
        priority: 'high'
      });
    }
    
    // LCP recommendations
    if (performance.largestContentfulPaint > 2500) {
      audit.recommendations.push({
        title: 'Optimize LCP',
        description: `LCP is ${Math.round(performance.largestContentfulPaint)}ms (should be <2.5s). Optimize hero images and critical content.`,
        priority: 'high'
      });
    }
    
    // Resource recommendations
    if (resources.slowResources.length > 3) {
      audit.recommendations.push({
        title: 'Reduce Slow Resources',
        description: `${resources.slowResources.length} resources take >1s to load. Consider lazy loading or optimization.`,
        priority: 'medium'
      });
    }
    
    if (resources.largeResources.length > 2) {
      audit.recommendations.push({
        title: 'Optimize Large Resources',
        description: `${resources.largeResources.length} resources are >500KB. Consider compression or lazy loading.`,
        priority: 'medium'
      });
    }
    
    if (resources.unoptimizedImages.length > 0) {
      audit.recommendations.push({
        title: 'Optimize Images',
        description: `${resources.unoptimizedImages.length} images are >200KB. Use WebP format and proper sizing.`,
        priority: 'medium'
      });
    }
    
    // Service Worker check
    if (!('serviceWorker' in navigator)) {
      audit.recommendations.push({
        title: 'Add Service Worker',
        description: 'Service Worker not supported. Consider adding for better caching and offline experience.',
        priority: 'low'
      });
    }
    
    // HTTPS check
    if (location.protocol !== 'https:') {
      audit.issues.push({
        title: 'HTTPS Required',
        description: 'Site is not using HTTPS. This affects security and performance.',
        priority: 'critical'
      });
    }
    
    // Mobile optimization
    if (window.innerWidth <= 768) {
      if (performance.loadTime > 3000) {
        audit.recommendations.push({
          title: 'Mobile Performance',
          description: `Mobile load time is ${Math.round(performance.loadTime)}ms (should be <3s). Optimize for mobile.`,
          priority: 'high'
        });
      }
    }
  }
  
  // Run audit on navigation
  if ('PerformanceObserver' in window) {
    const navigationObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach(entry => {
        if (entry.entryType === 'navigation') {
          setTimeout(runPerformanceAudit, 1000);
        }
      });
    });
    navigationObserver.observe({ entryTypes: ['navigation'] });
  }
  
})();
